import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSafeSupabaseClient, isSupabaseAvailable, TABLES } from '../lib/supabase';

const VerificationContext = createContext();

export const useVerification = () => {
  const context = useContext(VerificationContext);
  if (!context) {
    throw new Error('useVerification must be used within a VerificationProvider');
  }
  return context;
};

export const VerificationProvider = ({ children }) => {
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [userVerificationStatus, setUserVerificationStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseError, setSupabaseError] = useState(null);

  // Load verification data from Supabase on mount
  useEffect(() => {
    let isMounted = true;

    // Check if Supabase is available before attempting to load data
    if (!isSupabaseAvailable()) {
      console.warn('⚠️ Supabase is not available - verification features disabled');
      setSupabaseError(new Error('Supabase client not available'));
      return;
    }

    if (isMounted) {
      loadVerificationData();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const loadVerificationData = async () => {
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        console.warn('⚠️ Supabase not available in loadVerificationData');
        return;
      }
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load user's verification requests
      const { data: requests, error: requestsError } = await supabase
        .from(TABLES.VERIFICATION_REQUESTS)
        .select('*')
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false });

      if (requestsError) {
        console.error('Error loading verification requests:', requestsError);
      } else {
        setVerificationRequests(requests || []);
      }

      // Load user's verification status
      const { data: status, error: statusError } = await supabase
        .from(TABLES.USER_VERIFICATION_STATUS)
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (statusError && statusError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error loading verification status:', statusError);
      } else if (status) {
        setUserVerificationStatus(prev => ({
          ...prev,
          [user.id]: {
            verified: status.verified,
            status: status.status,
            adminNotes: status.admin_notes || '',
            updatedAt: status.updated_at
          }
        }));
      }
    } catch (error) {
      // Handle both Supabase initialization errors and query errors gracefully
      console.error('Error loading verification data:', error);
      setSupabaseError(error);
      // Don't throw - allow app to continue without verification data
    }
  };

  const submitVerificationRequest = async (requestData) => {
    setIsLoading(true);
    
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        return { success: false, error: 'Supabase client not available' };
      }
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user already has a pending or approved request
      const { data: existingRequests, error: checkError } = await supabase
        .from(TABLES.VERIFICATION_REQUESTS)
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['pending', 'approved']);

      if (checkError) {
        console.error('Error checking existing requests:', checkError);
        return { success: false, error: checkError.message };
      }

      if (existingRequests && existingRequests.length > 0) {
        const latestRequest = existingRequests[0];
        return { 
          success: false, 
          error: `You already have a ${latestRequest.status} verification request` 
        };
      }

      // Submit new verification request
      const { data, error } = await supabase
        .from(TABLES.VERIFICATION_REQUESTS)
        .insert([
          {
            user_id: user.id,
            wallet_address: requestData.walletAddress,
            display_name: requestData.displayName,
            email: requestData.email,
            additional_info: requestData.additionalInfo,
            status: 'pending',
            submitted_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error submitting verification request:', error);
        return { success: false, error: error.message };
      }

      // Reload verification data
      await loadVerificationData();

      return { success: true, data };
    } catch (error) {
      console.error('Error submitting verification request:', error);
      return { success: false, error: error?.message ?? 'Supabase not available' };
    } finally {
      setIsLoading(false);
    }
  };

  const checkVerificationStatus = async (walletAddress) => {
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        return null;
      }
      
      const { data, error } = await supabase
        .from(TABLES.USER_VERIFICATION_STATUS)
        .select('*')
        .eq('wallet_address', walletAddress)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking verification status:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error checking verification status:', error);
      return null;
    }
  };

  const isVerified = (userId) => {
    const status = userVerificationStatus[userId];
    return status && status.verified === true && status.status === 'approved';
  };

  const value = {
    verificationRequests,
    userVerificationStatus,
    isLoading,
    supabaseError,  // Expose error state so UI can show degraded mode
    submitVerificationRequest,
    checkVerificationStatus,
    isVerified,
    loadVerificationData
  };

  return (
    <VerificationContext.Provider value={value}>
      {children}
    </VerificationContext.Provider>
  );
};
